import type { TreeNode } from 'primevue/treenode';

import { Boolean, Null, Number, Optional, Record, Static, String, Unknown } from 'runtypes';

export const SharedQueriesTreeAsListRunType = Record({
	desktopApplicationItemId: String.Or(Null),
	id: String,
	name: String.Or(Null),
	serializedDesktopModuleFilterMemento: String.Or(Null),
	serializedIdoIdoListToolbarHiddenDetailsSearchString: String.Or(Null),
});

export type UnserializedDesktopModuleFilterMemento = {
	desktopModuleBoxIds: Array<string>;
	desktopModuleCustomFilterCondition: string;
	desktopModuleFilterMementoVersion: number;
	desktopModuleToolbarButtonArchivFilterCondition: boolean;
	desktopModuleToolbarButtonGlobalizationFilterCondition: boolean;
	desktopModuleToolbarCycleButtonDirection:
		| 'DesktopModuleToolbarCycleButtonDirectionBidirectional'
		| 'DesktopModuleToolbarCycleButtonDirectionIncoming'
		| 'DesktopModuleToolbarCycleButtonDirectionOutgoing';
	desktopModuleToolbarHiddenfieldSearchFieldCondition: string;
	desktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCondition:
		| 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll'
		| 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom'
		| 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonMine'
		| 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonOthers'
		| 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished';
	desktopModuleToolbarSegmentedButtonTimeRangeFilterCondition:
		| 'DesktopModuleToolbarSegmentedButtonTimeRangeSelectionButtonAll'
		| 'DesktopModuleToolbarSegmentedButtonTimeRangeSelectionButtonDay'
		| 'DesktopModuleToolbarSegmentedButtonTimeRangeSelectionButtonMonth'
		| 'DesktopModuleToolbarSegmentedButtonTimeRangeSelectionButtonWeek';
	desktopModuleToolbarSplitButtonUserFilter: Array<number>;
	desktopModuleToolbarTextfieldSearchFieldCondition: string;
};

export interface SharedQueryIdoDocumentBinding {
	categoryFilter: Array<string>;
	idoLinkings: Array<{ idoId: number; instanceId: number }>;
}

export type SharedQueriesTreeAsList = Static<typeof SharedQueriesTreeAsListRunType>;

export const OrganizationTreeNodeRunType = Record({
	activityCount: Optional(Number.Or(String).Or(Null)),
	children: Optional(Unknown),
	createdByTheAuthenticatedUser: Optional(Boolean.Or(String).Or(Null)),
	creatorInitial: Optional(String.Or(Null)),
	creatorUserId: Optional(Number.Or(String).Or(Null)),
	desktopApplicationItemId: Optional(String.Or(Null)),
	iconCls: Optional(String.Or(Null)),
	id: String,
	identifier: Optional(String.Or(Null)),
	idoIdoListToolbarCalculationIsActive: Optional(Boolean.Or(String).Or(Null)),
	idoInstanceId: Optional(String.Or(Null)),
	leaf: Optional(Boolean.Or(String).Or(Null)),
	modelType: Optional(String.Or(Null)),
	name: String,
	serializedDesktopModuleFilterMemento: Optional(String.Or(Null)),
	serializedIdoIdoListToolbarHiddenDetailsSearchString: Optional(String.Or(Null)),
	serializedUsersAlreadySharedWith: Optional(String.Or(Null)),
	serializedUsersToShareQueryWith: Optional(String.Or(Null)),
	serializedVirtualGroups: Optional(String.Or(Null)),
	text: Optional(String.Or(Null)),
	type: String,
});

export type OrganizationTreeNode = Static<typeof OrganizationTreeNodeRunType>;
export type NotebookTreeNode = OrganizationTreeNode;
export type SharedQueryTreeNode = OrganizationTreeNode;

export interface SharedQueryShareUser {
	displayName: string;
	id: string;
}

export interface SharedQueryVirtualGroup {
	id: string;
	name: string;
}

export type FolderExplorerTreeName = 'notebooks' | 'sharedQueries';
export type FolderExplorerNodeType = 'notebook' | 'sharedQueriesContainer' | 'sharedQuery';
export type FolderExplorerNodeActionState = 'editable' | 'readonly' | 'unsupported';

export interface FolderExplorerTreeNodeData {
	actionState: FolderExplorerNodeActionState;
	activityCount: number;
	createdByTheAuthenticatedUser: boolean;
	creatorInitial: null | string;
	creatorUserId: null | number;
	desktopApplicationItemId: null | string;
	identifier: null | string;
	idoIdoListToolbarCalculationIsActive: boolean;
	idoInstanceId: null | string;
	modelType: null | string;
	rawName: string;
	serializedDesktopModuleFilterMemento: null | string;
	serializedIdoIdoListToolbarHiddenDetailsSearchString: null | string;
	shareableUsers: SharedQueryShareUser[];
	shareableVirtualGroups: SharedQueryVirtualGroup[];
	sharedWithUsers: SharedQueryShareUser[];
	treeName: FolderExplorerTreeName;
	type: FolderExplorerNodeType;
}

export interface FolderExplorerTreeNode extends TreeNode {
	children?: FolderExplorerTreeNode[];
	data: FolderExplorerTreeNodeData;
	key: string;
	label: string;
	leaf: boolean;
	type: FolderExplorerNodeType;
}

export const IdoFavoriteRunType = Record({
	idoId: Number,
	idoInstanceId: Number,
	instance: String,
	instanceColor: String,
	position: Number,
	title: String,
});

export type IdoFavorite = Static<typeof IdoFavoriteRunType>;
